import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { CITIES } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SERVICE_OPTIONS = ["Not cooling", "Leaking water", "Strange noise", "Maintenance / tune-up", "New system estimate", "Other"];
const BEST_TIMES = ["ASAP", "Morning", "Afternoon", "Evening"];

const LeadSuccess = () => (
  <div data-testid="lead-form-success" className="bg-white border border-zinc-200 p-8 text-center rounded-sm">
    <CheckCircle2 className="w-12 h-12 text-brand mx-auto mb-4" />
    <h3 className="font-heading font-bold text-2xl text-brand-ink mb-2">Got it!</h3>
    <p className="text-zinc-600">We'll call you within the hour during business hours. For emergencies, call <a href="tel:7865121929" className="text-brand font-medium">786-512-1929</a> any time.</p>
  </div>
);

const LeadFields = ({ form, set, setForm, serviceOptions }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <Input data-testid="lead-name-input" required placeholder="Full name" value={form.name} onChange={set("name")} className="rounded-sm" />
    <Input data-testid="lead-phone-input" required placeholder="Phone" type="tel" value={form.phone} onChange={set("phone")} className="rounded-sm" />
    <Input data-testid="lead-email-input" required placeholder="Email" type="email" value={form.email} onChange={set("email")} className="rounded-sm" />
    <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
      <SelectTrigger data-testid="lead-city-select" className="rounded-sm"><SelectValue placeholder="Your city" /></SelectTrigger>
      <SelectContent>
        {CITIES.map((c) => <SelectItem key={c.slug} value={c.name} data-testid={`lead-city-${c.slug}`}>{c.name}</SelectItem>)}
        <SelectItem value="Other">Other / Not listed</SelectItem>
      </SelectContent>
    </Select>
    <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
      <SelectTrigger data-testid="lead-service-select" className="rounded-sm"><SelectValue placeholder="What do you need help with?" /></SelectTrigger>
      <SelectContent>
        {serviceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
      </SelectContent>
    </Select>
    <Select value={form.best_time} onValueChange={(v) => setForm({ ...form, best_time: v })}>
      <SelectTrigger data-testid="lead-best-time-select" className="rounded-sm"><SelectValue placeholder="Best time to call" /></SelectTrigger>
      <SelectContent>
        {BEST_TIMES.map((t) => <SelectItem key={t} value={t} data-testid={`lead-best-time-${t.toLowerCase()}`}>{t}</SelectItem>)}
      </SelectContent>
    </Select>
    <Textarea data-testid="lead-message-input" placeholder="Tell us more (optional)" value={form.message} onChange={set("message")} className="sm:col-span-2 rounded-sm" rows={3} />
  </div>
);

export const LeadForm = ({ defaultService = "", building = "", heading = "Get AC Help", subtext = "Tell us what's going on and we'll call you back fast — usually within the hour." }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: defaultService, city: "", best_time: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const serviceOptions = defaultService && !SERVICE_OPTIONS.includes(defaultService)
    ? [defaultService, ...SERVICE_OPTIONS]
    : SERVICE_OPTIONS;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await api.post("/leads", { ...form, building });
      track("lead_form_submit", { service: form.service, city: form.city });
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong. Please call us at 786-512-1929.");
    }
  };

  if (status === "success") return <LeadSuccess />;

  return (
    <form data-testid="lead-form" onSubmit={submit} className="bg-white border border-zinc-200 p-6 sm:p-8 rounded-sm">
      <h3 className="font-heading font-bold text-2xl text-brand-ink mb-1">{heading}</h3>
      <p className="text-sm text-zinc-600 mb-6">{subtext}</p>
      <LeadFields form={form} set={set} setForm={setForm} serviceOptions={serviceOptions} />
      {error && <p data-testid="lead-form-error" className="text-sm text-brand-red mt-3">{error}</p>}
      <button data-testid="lead-form-submit-btn" type="submit" disabled={status === "loading"}
        className="mt-6 w-full bg-brand-red hover:bg-[#B02222] disabled:opacity-60 text-white font-bold py-3.5 rounded-sm transition-colors duration-200 flex items-center justify-center gap-2">
        {status === "loading" && <Loader2 className="w-4 h-4 animate-spin" />}
        Get AC Help Now
      </button>
      <p className="text-xs text-zinc-500 mt-3 text-center">No spam, ever. We'll only contact you about your request.</p>
    </form>
  );
};
